import React, { FC, useCallback } from "react";

import Tooltip from "antd/es/tooltip";
import { defineMessages, useIntl } from "react-intl";

import { Color } from "@mapmycustomers/shared";
import { Territory } from "@mapmycustomers/shared/types/entity";
import { CountryState } from "@mapmycustomers/shared/types/territory/CountryState";
import { AvatarWithName } from "@mapmycustomers/ui";

import Checkbox from "@app/component/input/Checkbox";
import anyColorToHex from "@app/util/colors/anyColorToHex";

import styles from "./TerritoryItem.module.scss";

const messages = defineMessages({
  count: {
    id: "map.layer.boundaries.territories.filterModal.item.count",
    defaultMessage: "{count} {count, plural, one {record} other {records}}",
    description: "Number of records in Territory item in the Filter Territories modal",
  },
  owner: {
    id: "map.layer.boundaries.territories.filterModal.item.owner",
    defaultMessage: "Owner: {user}",
    description: "Owner text in Territory item in the Filter Territories modal",
  },
});

export interface TerritoryItemProps {
  countryStateDisplayNames: Record<CountryState, string>;
  onSelectionChange?: (territory: Territory, selected: boolean) => void;
  selected?: boolean;
  territory: Territory;
}

const TerritoryItem: FC<TerritoryItemProps> = ({
  countryStateDisplayNames,
  onSelectionChange,
  selected,
  territory,
}) => {
  const intl = useIntl();

  const handleChange = useCallback(
    (checked: boolean) => {
      onSelectionChange?.(territory, checked);
    },
    [onSelectionChange, territory]
  );

  const handleClick = useCallback(() => {
    onSelectionChange?.(territory, !selected);
  }, [onSelectionChange, territory, selected]);

  const regionNames = (territory.territoryDetail.region ?? []).map(
    (region) => countryStateDisplayNames[region] ?? region
  );
  const regionsString = regionNames.join(", ");

  return (
    <div className={styles.container} onClick={handleClick}>
      <Checkbox checked={selected} onChange={handleChange} />
      <div className={styles.info}>
        <div className={styles.title}>{territory.name}</div>
        <div className={styles.description}>
          <span
            className={styles.colorDot}
            style={{ backgroundColor: anyColorToHex(territory.color ?? Color.BLACK) }}
          />
          <span>{intl.formatMessage(messages.count, { count: territory.count ?? 0 })}</span>
          <span>{" • "}</span>
          {regionNames.length > 0 && (
            <Tooltip title={regionsString}>
              <span className={styles.regions}>{regionsString}</span>
            </Tooltip>
          )}
          {regionNames.length > 0 ? <span> • </span> : null}
          <span className={styles.owner}>
            {intl.formatMessage(messages.owner, {
              user: <AvatarWithName size="xs" user={territory.user} />,
            })}
          </span>
        </div>
      </div>
    </div>
  );
};

export default TerritoryItem;
